import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Collapse,
} from '@mui/material';
import {
  VisibilityOff,
  Visibility,
  RemoveRedEye,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Search,
  Close as CloseIcon,
  ArrowBack,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import assetCategoryService from '../services/assetCategoryService';
import { useActionLock } from '../hooks/useActionLock';
import ActionButton from '../components/ActionButton';

const Assets = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showAddForm, setShowAddForm] = useState(false);
  const [viewingAsset, setViewingAsset] = useState(null);
  const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false);
  const [assetToToggle, setAssetToToggle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedAssetId, setExpandedAssetId] = useState(null);
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    category: '',
    categoryDescription: '',
    field1: '',
    field2: '',
    field3: '',
    field4: '',
  });

  // API state (action locks prevent double submit)
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, runSave] = useActionLock();
  const [visibilityLocked, runToggleVisibility] = useActionLock();
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [totalCount, setTotalCount] = useState(0);

  // Fetch asset categories from backend with pagination
  const fetchAssets = async (customPage) => {
    setLoading(true);
    setError('');

    // Ensure customPage is a valid number (not an event object)
    const pageToUse = typeof customPage === 'number' ? customPage : page;

    const params = {
      ordering: 'order',
      page: pageToUse + 1,
      page_size: rowsPerPage,
    };

    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    const result = await assetCategoryService.getAssetCategories(params);

    if (result.success) {
      // Map API response to component structure
      const mappedAssets = (result.data.results || result.data || []).map(item => ({
        id: item.id,
        category: item.asset_category_value,
        categoryDescription: item.category_description || '',
        field1: item.asset_field_1_value,
        field2: item.asset_field_2_value,
        field3: item.asset_field_3_value,
        field4: item.asset_field_4_value,
        order: item.order,
        visible: item.is_active,
      }));
      setAssets(mappedAssets);
      setTotalCount(result.data.count || mappedAssets.length);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  // Initial fetch and when page/rowsPerPage changes
  useEffect(() => {
    fetchAssets();
  }, [page, rowsPerPage]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const refreshAssets = async () => {
    await fetchAssets();
  };

  const handleAddNew = () => {
    setFormData({
      category: '',
      categoryDescription: '',
      field1: '',
      field2: '',
      field3: '',
      field4: '',
    });
    setShowAddForm(true);
  };

  const handleView = (asset) => {
    setViewingAsset(asset);
  };

  const handleBack = () => {
    setViewingAsset(null);
  };

  const handleUpdate = async () => {
    // Validate all required fields
    if (!formData.category?.trim()) {
      showSnackbar('Please fill in Asset Category Value', 'error');
      return;
    }

    if (!formData.field1?.trim()) {
      showSnackbar('Please fill in Asset Field 1 Value', 'error');
      return;
    }

    if (!formData.field2?.trim()) {
      showSnackbar('Please fill in Asset Field 2 Value', 'error');
      return;
    }

    if (!formData.field3?.trim()) {
      showSnackbar('Please fill in Asset Field 3 Value', 'error');
      return;
    }

    if (!formData.field4?.trim()) {
      showSnackbar('Please fill in Asset Field 4 Value', 'error');
      return;
    }

    const apiData = {
      asset_category_value: formData.category,
      category_description: formData.categoryDescription || '',
      asset_field_1_value: formData.field1,
      asset_field_2_value: formData.field2,
      asset_field_3_value: formData.field3,
      asset_field_4_value: formData.field4,
      is_active: true,
    };

    const result = await assetCategoryService.createAssetCategory(apiData);

    if (result.success) {
      showSnackbar('Asset created successfully', 'success');
      await refreshAssets();
      setShowAddForm(false);
    } else {
      showSnackbar(result.error, 'error');
    }
  };

  const handleMoveUp = async (asset) => {
    const result = await assetCategoryService.moveUp(asset.id);
    if (result.success) {
      showSnackbar('Asset moved up successfully', 'success');
      await refreshAssets();
    } else {
      showSnackbar(result.error, 'error');
    }
  };

  const handleMoveDown = async (asset) => {
    const result = await assetCategoryService.moveDown(asset.id);
    if (result.success) {
      showSnackbar('Asset moved down successfully', 'success');
      await refreshAssets();
    } else {
      showSnackbar(result.error, 'error');
    }
  };

  const handleToggleVisibilityClick = (asset) => {
    setAssetToToggle(asset);
    setVisibilityDialogOpen(true);
  };

  const handleToggleVisibilityConfirm = async () => {
    const result = await assetCategoryService.toggleActive(assetToToggle.id);
    if (result.success) {
      showSnackbar(`Asset ${assetToToggle.visible ? 'hidden' : 'unhidden'} successfully`, 'success');
      await refreshAssets();
    } else {
      showSnackbar(result.error, 'error');
    }
    setVisibilityDialogOpen(false);
    setAssetToToggle(null);
  };

  const handleToggleVisibilityCancel = () => {
    setVisibilityDialogOpen(false);
    setAssetToToggle(null);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await assetCategoryService.exportToExcel();

      if (result.success) {
        // Create a blob URL and trigger download
        const blob = new Blob([result.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `asset_categories_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        showSnackbar('Excel file downloaded successfully');
      } else {
        showSnackbar(result.error || 'Failed to export Excel', 'error');
      }
    } catch (error) {
      showSnackbar('An error occurred while exporting', 'error');
    } finally {
      setExporting(false);
    }
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      if (page === 0) {
        fetchAssets(0);
      } else {
        setPage(0);
      }
    }
  };

  // Handle search input change with reset to page 0
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // View Asset Page
  if (viewingAsset) {
    return (
      <Box sx={{
        backgroundColor: '#F8FAFC',
        minHeight: '100vh',
        padding: { xs: 2, sm: 2.5, md: 3 }
      }}>
        {/* Header with Back Button */}
        <Paper
          elevation={0}
          sx={{
            padding: { xs: 1.5, sm: 2 },
            marginBottom: { xs: 2, md: 3 },
            borderRadius: 2,
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            color: 'white',
            border: '1px solid #ECF0F1',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <IconButton
              onClick={handleBack}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' } }}>
              View Asset Details
            </Typography>
          </Box>
        </Paper>

        {/* Asset Details Card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #ECF0F1',
          }}
        >
          {/* Card Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
              padding: '16px 24px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: '18px',
              }}
            >
              {viewingAsset.category}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Box
                sx={{
                  backgroundColor: viewingAsset.visible ? 'rgba(16, 185, 129, 0.2)' : 'rgba(220, 38, 38, 0.2)',
                  color: viewingAsset.visible ? '#10B981' : '#DC2626',
                  padding: '4px 12px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                {viewingAsset.visible ? 'Active' : 'Hidden'}
              </Box>
            </Box>
          </Box>

          {/* Card Content */}
          <Box sx={{ padding: 3 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 3,
              }}
            >
              {/* Field 1 */}
              <Box
                sx={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '12px',
                  padding: 2.5,
                  border: '1px solid #E2E8F0',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 1,
                  }}
                >
                  Field 1 Value
                </Typography>
                <Typography
                  sx={{
                    color: '#1E293B',
                    fontSize: '15px',
                    fontWeight: 600,
                  }}
                >
                  {viewingAsset.field1 || '-'}
                </Typography>
              </Box>

              {/* Field 2 */}
              <Box
                sx={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '12px',
                  padding: 2.5,
                  border: '1px solid #E2E8F0',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 1,
                  }}
                >
                  Field 2 Value
                </Typography>
                <Typography
                  sx={{
                    color: '#1E293B',
                    fontSize: '15px',
                    fontWeight: 600,
                  }}
                >
                  {viewingAsset.field2 || '-'}
                </Typography>
              </Box>

              {/* Field 3 */}
              <Box
                sx={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '12px',
                  padding: 2.5,
                  border: '1px solid #E2E8F0',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 1,
                  }}
                >
                  Field 3 Value
                </Typography>
                <Typography
                  sx={{
                    color: '#1E293B',
                    fontSize: '15px',
                    fontWeight: 600,
                  }}
                >
                  {viewingAsset.field3 || '-'}
                </Typography>
              </Box>

              {/* Field 4 */}
              <Box
                sx={{
                  backgroundColor: '#F8FAFC',
                  borderRadius: '12px',
                  padding: 2.5,
                  border: '1px solid #E2E8F0',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#64748b',
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: 1,
                  }}
                >
                  Field 4 Value
                </Typography>
                <Typography
                  sx={{
                    color: '#1E293B',
                    fontSize: '15px',
                    fontWeight: 600,
                  }}
                >
                  {viewingAsset.field4 || '-'}
                </Typography>
              </Box>
            </Box>

            {/* Back Button */}
            <Box sx={{ marginTop: 4, display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{
                  borderColor: '#E2E8F0',
                  color: '#64748b',
                  textTransform: 'none',
                  borderRadius: '10px',
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#4E83BD',
                    backgroundColor: '#F8FAFC',
                  },
                }}
              >
                Back to List
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  // List View
  return (
    <Box sx={{
      backgroundColor: '#F8FAFC',
      minHeight: '100vh',
      padding: { xs: 2, sm: 2.5, md: 3 }
    }}>
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2 },
          marginBottom: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
          color: 'white',
          border: '1px solid #ECF0F1',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 0 },
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '18px', sm: '20px' } }}>
            Manage Asset
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              onClick={handleAddNew}
              sx={{
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: { xs: '11px', sm: '13px' },
                padding: { xs: '6px 12px', sm: '8px 16px' },
                borderRadius: '6px',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                },
              }}
            >
              Add Asset
            </Button>

            <Button
              variant="contained"
              onClick={handleExport}
              disabled={exporting}
              sx={{
                textTransform: 'none',
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '13px',
                padding: '8px 16px',
                borderRadius: '6px',
                backdropFilter: 'blur(10px)',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  color: 'rgba(255,255,255,0.7)',
                },
              }}
            >
              {exporting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={14} sx={{ color: 'white' }} />
                  <span>Exporting...</span>
                </Box>
              ) : (
                'Export as Excel'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Search and Count */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        <TextField
          size="small"
          placeholder="Search by category..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyPress={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#64748b', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: '300px',
            backgroundColor: '#FFFFFF',
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#ECF0F1',
              },
              '&:hover fieldset': {
                borderColor: '#4E83BD',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#4E83BD',
              },
            },
          }}
        />
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} sx={{ color: '#4E83BD' }} />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ marginBottom: 3 }}>
          {error}
        </Alert>
      )}

      {/* Desktop Table View */}
      {!loading && !error && !isMobile && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #ECF0F1',
            marginBottom: 3,
          }}
        >
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: '#4E83BD',
                  }}
                >
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    Asset_Category_Value
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      fontSize: '13px',
                      borderBottom: 'none',
                      padding: '12px 16px',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assets.map((asset, index) => (
                  <TableRow
                    key={asset.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F8FAFC',
                      '&:hover': {
                        backgroundColor: '#E8F4F8',
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        color: '#2C3E50',
                        fontSize: '14px',
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                      }}
                    >
                      {asset.category}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        borderBottom: '1px solid #ECF0F1',
                        padding: '12px 16px',
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveUp(asset)}
                          disabled={index === 0}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                            '&.Mui-disabled': {
                              color: '#CBD5E1',
                              backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            },
                          }}
                        >
                          <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDown(asset)}
                          disabled={index === assets.length - 1}
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                            '&.Mui-disabled': {
                              color: '#CBD5E1',
                              backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            },
                          }}
                        >
                          <KeyboardArrowDown fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={() => handleView(asset)}
                          title="View Asset"
                          sx={{
                            color: '#4E83BD',
                            backgroundColor: 'rgba(78, 131, 189, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(78, 131, 189, 0.2)',
                            },
                          }}
                        >
                          <RemoveRedEye fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleVisibilityClick(asset)}
                          title={asset.visible ? 'Hide Asset' : 'Unhide Asset'}
                          sx={{
                            color: asset.visible ? '#DC2626' : '#059669',
                            backgroundColor: asset.visible ? 'rgba(220, 38, 38, 0.1)' : 'rgba(5, 150, 105, 0.1)',
                            '&:hover': {
                              backgroundColor: asset.visible ? 'rgba(220, 38, 38, 0.2)' : 'rgba(5, 150, 105, 0.2)',
                            },
                          }}
                        >
                          {asset.visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Empty State */}
          {assets.length === 0 && (
            <Box sx={{ padding: 6, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#64748b', marginBottom: 1, fontSize: '18px', fontWeight: 600 }}>
                No Data Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', marginBottom: 3 }}>
                {searchQuery ? 'No assets match your search criteria' : 'Get started by adding your first asset'}
              </Typography>
              {!searchQuery && (
                <Button
                  variant="contained"
                  onClick={handleAddNew}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#4E83BD',
                    '&:hover': {
                      backgroundColor: '#3A6A99',
                    },
                  }}
                >
                  Add Asset
                </Button>
              )}
            </Box>
          )}

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: '1px solid #ECF0F1',
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                color: '#2C3E50',
              },
            }}
          />
        </Paper>
      )}

      {/* Mobile Card View */}
      {!loading && !error && isMobile && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {assets.map((asset, index) => (
            <Paper
              key={asset.id}
              elevation={0}
              sx={{
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #ECF0F1',
                backgroundColor: '#FFFFFF',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(78, 131, 189, 0.15)',
                  borderColor: '#4E83BD',
                },
              }}
            >
              {/* Card Header - Status */}
              <Box
                sx={{
                  backgroundColor: expandedAssetId === asset.id ? '#3A6A99' : '#4E83BD',
                  padding: '12px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'background-color 0.3s ease',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: asset.visible ? 'rgba(16, 185, 129, 0.2)' : 'rgba(220, 38, 38, 0.2)',
                    color: asset.visible ? '#10B981' : '#DC2626',
                    padding: '4px 12px',
                    borderRadius: '16px',
                    fontSize: '11px',
                    fontWeight: 600,
                  }}
                >
                  {asset.visible ? 'Active' : 'Hidden'}
                </Box>
              </Box>

              {/* Card Title Section - Always Visible */}
              <Box
                onClick={() => setExpandedAssetId(expandedAssetId === asset.id ? null : asset.id)}
                sx={{
                  padding: '14px 16px',
                  backgroundColor: '#FFFFFF',
                  borderBottom: expandedAssetId === asset.id ? '1px solid #ECF0F1' : 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                  '&:active': {
                    backgroundColor: '#F8FAFC',
                  },
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '10px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 0.5,
                    }}
                  >
                    Asset Category
                  </Typography>
                  <Typography
                    sx={{
                      color: '#1E293B',
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: 1.4,
                    }}
                  >
                    {asset.category}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  sx={{
                    color: '#4E83BD',
                    flexShrink: 0,
                    transition: 'transform 0.3s ease',
                    transform: expandedAssetId === asset.id ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  {expandedAssetId === asset.id ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>

              {/* Expanded Content - Asset Fields */}
              <Collapse in={expandedAssetId === asset.id} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    padding: '14px 16px',
                    backgroundColor: '#F8FAFC',
                    borderBottom: '1px solid #ECF0F1',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 2,
                  }}
                >
                  {/* Field 1 */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 0.5,
                      }}
                    >
                      Field 1
                    </Typography>
                    <Typography
                      sx={{
                        color: '#2C3E50',
                        fontSize: '12px',
                        fontWeight: 500,
                        wordBreak: 'break-word',
                      }}
                    >
                      {asset.field1 || '-'}
                    </Typography>
                  </Box>

                  {/* Field 2 */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 0.5,
                      }}
                    >
                      Field 2
                    </Typography>
                    <Typography
                      sx={{
                        color: '#2C3E50',
                        fontSize: '12px',
                        fontWeight: 500,
                        wordBreak: 'break-word',
                      }}
                    >
                      {asset.field2 || '-'}
                    </Typography>
                  </Box>

                  {/* Field 3 */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 0.5,
                      }}
                    >
                      Field 3
                    </Typography>
                    <Typography
                      sx={{
                        color: '#2C3E50',
                        fontSize: '12px',
                        fontWeight: 500,
                        wordBreak: 'break-word',
                      }}
                    >
                      {asset.field3 || '-'}
                    </Typography>
                  </Box>

                  {/* Field 4 */}
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#64748b',
                        fontSize: '10px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 0.5,
                      }}
                    >
                      Field 4
                    </Typography>
                    <Typography
                      sx={{
                        color: '#2C3E50',
                        fontSize: '12px',
                        fontWeight: 500,
                        wordBreak: 'break-word',
                      }}
                    >
                      {asset.field4 || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Collapse>

              {/* Card Actions - Always Visible */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  padding: '10px 12px',
                  borderTop: '1px solid #ECF0F1',
                  backgroundColor: '#F8FAFC',
                  gap: 0.5,
                  flexWrap: 'wrap',
                }}
              >
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleView(asset)}
                    title="View"
                    sx={{
                      color: '#4E83BD',
                      backgroundColor: 'rgba(78, 131, 189, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(78, 131, 189, 0.2)',
                      },
                      padding: '6px',
                    }}
                  >
                    <RemoveRedEye fontSize="small" sx={{ fontSize: '16px' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleMoveUp(asset)}
                    disabled={index === 0}
                    title="Move Up"
                    sx={{
                      color: '#4E83BD',
                      backgroundColor: 'rgba(78, 131, 189, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(78, 131, 189, 0.2)',
                      },
                      '&.Mui-disabled': {
                        color: '#94a3b8',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      },
                      padding: '6px',
                    }}
                  >
                    <KeyboardArrowUp fontSize="small" sx={{ fontSize: '16px' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleMoveDown(asset)}
                    disabled={index === assets.length - 1}
                    title="Move Down"
                    sx={{
                      color: '#4E83BD',
                      backgroundColor: 'rgba(78, 131, 189, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(78, 131, 189, 0.2)',
                      },
                      '&.Mui-disabled': {
                        color: '#94a3b8',
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      },
                      padding: '6px',
                    }}
                  >
                    <KeyboardArrowDown fontSize="small" sx={{ fontSize: '16px' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleVisibilityClick(asset)}
                    title={asset.visible ? 'Hide' : 'Show'}
                    sx={{
                      color: asset.visible ? '#DC2626' : '#059669',
                      backgroundColor: asset.visible ? 'rgba(220, 38, 38, 0.1)' : 'rgba(5, 150, 105, 0.1)',
                      '&:hover': {
                        backgroundColor: asset.visible ? 'rgba(220, 38, 38, 0.2)' : 'rgba(5, 150, 105, 0.2)',
                      },
                      padding: '6px',
                    }}
                  >
                    {asset.visible ? <VisibilityOff fontSize="small" sx={{ fontSize: '16px' }} /> : <Visibility fontSize="small" sx={{ fontSize: '16px' }} />}
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))}

          {/* Empty State for Mobile */}
          {assets.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid #ECF0F1',
                padding: 4,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ color: '#64748b', marginBottom: 1, fontSize: '16px', fontWeight: 600 }}>
                No Data Found
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', marginBottom: 3, fontSize: '13px' }}>
                {searchQuery ? 'No assets match your search criteria' : 'Get started by adding your first asset'}
              </Typography>
              {!searchQuery && (
                <Button
                  variant="contained"
                  onClick={handleAddNew}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#4E83BD',
                    '&:hover': {
                      backgroundColor: '#3A6A99',
                    },
                  }}
                >
                  Add Asset
                </Button>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* Hide/Unhide Confirmation Dialog */}
      <Dialog
        open={visibilityDialogOpen}
        onClose={handleToggleVisibilityCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 1,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                backgroundColor: assetToToggle?.visible ? '#FEE2E2' : '#DCFCE7',
                borderRadius: '50%',
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {assetToToggle?.visible ? (
                <VisibilityOff sx={{ color: '#DC2626', fontSize: 28 }} />
              ) : (
                <Visibility sx={{ color: '#2E7D32', fontSize: 28 }} />
              )}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
              {assetToToggle?.visible ? 'Hide Asset' : 'Unhide Asset'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#2C3E50', fontSize: '14px', lineHeight: 1.6 }}>
            Do you want to {assetToToggle?.visible ? 'hide' : 'unhide'} the Asset{' '}
            <strong>"{assetToToggle?.category}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleToggleVisibilityCancel}
            variant="outlined"
            disabled={visibilityLocked}
            sx={{
              borderColor: '#ECF0F1',
              color: '#2C3E50',
              textTransform: 'none',
              paddingX: 2.5,
              fontSize: '14px',
              '&:hover': {
                borderColor: '#4E83BD',
                backgroundColor: '#F8FAFC',
              },
            }}
          >
            Cancel
          </Button>
          <ActionButton
            onClick={() => runToggleVisibility(handleToggleVisibilityConfirm)}
            variant="contained"
            loading={visibilityLocked}
            loadingText={assetToToggle?.visible ? 'Hiding...' : 'Unhiding...'}
            sx={{
              backgroundColor: assetToToggle?.visible ? '#DC2626' : '#2E7D32',
              textTransform: 'none',
              paddingX: 3,
              fontSize: '14px',
              '&:hover': {
                backgroundColor: assetToToggle?.visible ? '#B91C1C' : '#047857',
              },
            }}
          >
            {assetToToggle?.visible ? 'Hide' : 'Unhide'}
          </ActionButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add New Asset Dialog */}
      <Dialog
        open={showAddForm}
        onClose={() => setShowAddForm(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            maxHeight: '90vh',
          },
        }}
      >
        {/* Dialog Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            padding: '16px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontSize: '18px',
            }}
          >
            Add New Asset
          </Typography>
          <IconButton
            onClick={() => setShowAddForm(false)}
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ padding: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              minHeight: '400px',
            }}
          >
            {/* Left Column - Asset Category */}
            <Box
              sx={{
                flex: '0 0 40%',
                padding: 3,
                backgroundColor: '#FFFFFF',
                borderRight: { md: '1px solid #ECF0F1' },
                borderBottom: { xs: '1px solid #ECF0F1', md: 'none' },
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  marginBottom: 3,
                }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: '24px',
                    backgroundColor: '#4E83BD',
                    borderRadius: '2px',
                  }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#1E293B',
                    fontSize: '16px',
                  }}
                >
                  Asset Category
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                }}
              >
                Category Value <span style={{ color: '#DC2626' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="Term Insurance"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#F8FAFC',
                    '& fieldset': {
                      borderColor: '#E2E8F0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#4E83BD',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4E83BD',
                    },
                  },
                }}
              />

              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 1,
                  marginTop: 2,
                }}
              >
                Category Description
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={formData.categoryDescription}
                onChange={(e) => setFormData({ ...formData, categoryDescription: e.target.value })}
                placeholder="Optional: Describe this asset category..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: '#F8FAFC',
                    '& fieldset': {
                      borderColor: '#E2E8F0',
                    },
                    '&:hover fieldset': {
                      borderColor: '#4E83BD',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4E83BD',
                    },
                  },
                }}
              />

              {/* Action Buttons */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  gap: 1.5,
                  marginTop: 4,
                  paddingTop: 2,
                  borderTop: '1px solid #E2E8F0',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setShowAddForm(false)}
                  disabled={saving}
                  sx={{
                    borderColor: '#E2E8F0',
                    color: '#64748b',
                    textTransform: 'none',
                    borderRadius: '10px',
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#CBD5E1',
                      backgroundColor: '#F8FAFC',
                    },
                  }}
                >
                  Cancel
                </Button>
                <ActionButton
                  variant="contained"
                  loading={saving}
                  onClick={() => runSave(handleUpdate)}
                  loadingText="Saving..."
                  sx={{
                    background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
                    textTransform: 'none',
                    borderRadius: '10px',
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(78, 131, 189, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #3A6A99 0%, #2D5580 100%)',
                      boxShadow: '0 6px 16px rgba(78, 131, 189, 0.5)',
                    },
                  }}
                >
                  Add Asset
                </ActionButton>
              </Box>
            </Box>

            {/* Right Column - Custom Fields */}
            <Box
              sx={{
                flex: '0 0 60%',
                padding: 3,
                backgroundColor: '#F8FAFC',
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  marginBottom: 3,
                }}
              >
                <Box
                  sx={{
                    width: '4px',
                    height: '24px',
                    backgroundColor: '#10B981',
                    borderRadius: '2px',
                  }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: '#1E293B',
                    fontSize: '16px',
                  }}
                >
                  Custom Fields Configuration
                </Typography>
              </Box>

              {/* Two Column Grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {/* Field 1 */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 1,
                    }}
                  >
                    Field 1 Value <span style={{ color: '#DC2626' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    value={formData.field1}
                    onChange={(e) => setFormData({ ...formData, field1: e.target.value })}
                    placeholder="Upload Policy"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderColor: '#E2E8F0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#4E83BD',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4E83BD',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Field 2 */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 1,
                    }}
                  >
                    Field 2 Value <span style={{ color: '#DC2626' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    value={formData.field2}
                    onChange={(e) => setFormData({ ...formData, field2: e.target.value })}
                    placeholder="Policy issuer's Name"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderColor: '#E2E8F0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#4E83BD',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4E83BD',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Field 3 */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 1,
                    }}
                  >
                    Field 3 Value <span style={{ color: '#DC2626' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    value={formData.field3}
                    onChange={(e) => setFormData({ ...formData, field3: e.target.value })}
                    placeholder="Policy Number"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderColor: '#E2E8F0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#4E83BD',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4E83BD',
                        },
                      },
                    }}
                  />
                </Box>

                {/* Field 4 */}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#64748b',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 1,
                    }}
                  >
                    Field 4 Value <span style={{ color: '#DC2626' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    value={formData.field4}
                    onChange={(e) => setFormData({ ...formData, field4: e.target.value })}
                    placeholder="Re-enter Policy Number"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderColor: '#E2E8F0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#4E83BD',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4E83BD',
                        },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Assets;
